// src/pages/Charts.jsx 
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { EmptyState } from '@/components/ui/empty-state.jsx';
import { apiCall } from '../utils/api';
import { isAnalystOrHigher } from '../utils/roles';
import { Plus, RefreshCw, Loader2, BarChart3, MoreVertical, Edit2, Copy, Code2, Trash2, Folder, Tag } from 'lucide-react';
import ManageAllTagsModal from '../components/modals/ManageAllTagsModal';
import FolderModal from '../components/modals/FolderModal';
import FolderSidebar from '../components/FolderSidebar';
import TagFilterChips from '../components/TagFilterChips';
import { API_BASE_URL } from '../config';
// Phase 3 step 10: cards for charts made with the new builder are drawn by
// ChartView and edit in the new builder; older charts keep the embed preview.
import ChartView from '../charts/ChartView';
import { editPath, isBuilderChart, viewProps } from './builder/savedChart';
import { Sparkles } from 'lucide-react';
// Phase 4 step 14a-3: deleting a chart a dashboard shows is refused by the server
// (CHART_IN_USE) and asked about here, instead of a bare "Failed to delete".
import { deleteChart } from '../charts/semanticApi';
import ChartInUseDialog, { deleteChartWithGuard } from './dashboards/ChartInUseDialog';

export default function Charts() {
  const navigate = useNavigate();
  
  const [charts, setCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    chartType: '',
    visibility: '',
    folderId: null,
    tagIds: [],
  });

  const [showTagsModal, setShowTagsModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [inUse, setInUse] = useState(null);          // 14a-3: { chartId, chartTitle, dashboards } while the server's refusal is shown
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // 14a-3: the delete flow shared by every card. force = "Delete anyway" from the dialog.
  const removeChart = async (chart, force = false) => {
    setDeleting(true);
    setDeleteError(null);
    try {
      const gone = await deleteChartWithGuard(deleteChart, chart.id, {
        force, onInUse: (dashboards) => setInUse({ chartId: chart.id, chartTitle: chart.title, dashboards }),
      });
      if (gone) await loadCharts();
    } catch (err) {
      setDeleteError(err && err.status === 403 ? "You don't have permission to delete charts." : `"${chart.title}" couldn't be deleted. Try again.`);
    } finally {
      setDeleting(false);
    }
  };

  const handleFilterChange = (key, value) => {
    console.log('Filter changed:', key, value);
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getFilteredCharts = () => {
    const filtered = charts.filter(chart => {
      const matchesSearch = !filters.search || 
        chart.title.toLowerCase().includes(filters.search.toLowerCase());
      
      const matchesChartType = !filters.chartType || 
        chart.chart_type === filters.chartType;
      
      const matchesVisibility = !filters.visibility || 
        chart.visibility === filters.visibility;
      
      const matchesFolder = filters.folderId === null || 
        chart.folder_id === filters.folderId;

      const matchesTags = filters.tagIds.length === 0 || 
        (chart.tags && filters.tagIds.some(tagId => chart.tags.includes(tagId)));
      
      return matchesSearch && matchesChartType && matchesVisibility && matchesFolder && matchesTags;
    });

    console.log('Total charts:', charts.length, 'Filtered:', filtered.length, 'Filters:', filters);
    return filtered;
  };

async function loadCharts() {
  setLoading(true);
  setError(null);
  
  try {
    const res = await apiCall('/charts/');
    if (!res.ok) {
      throw new Error('Failed to load charts');
    }
    const data = await res.json();
    const chartsData = data.charts || [];
    
    // Load tags for each chart
    const chartsWithTags = await Promise.all(
      chartsData.map(async (chart) => {
        try {
          const tagsRes = await apiCall(`/charts/${chart.id}/tags`);
          if (tagsRes.ok) {
            const tags = await tagsRes.json();
            return {
              ...chart,
              tags: tags.map(t => t.id) // Array of tag IDs
            };
          }
        } catch (err) {
          console.error(`Failed to load tags for chart ${chart.id}:`, err);
        }
        return { ...chart, tags: [] };
      })
    );
    
    setCharts(chartsWithTags);
  } catch (err) {
    console.error('Failed to load charts:', err);
    setError(err.message || 'Failed to load charts');
  } finally {
    setLoading(false);
  }
}

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadCharts();
    setTimeout(() => setRefreshing(false), 500);
  };

  const handleNewChart = () => {
    navigate('/charts/build');
  };

  const handleFolderSelect = (folderId) => {
    setFilters(prev => ({ ...prev, folderId }));
  };

  const handleCreateFolder = () => {
    setFolderToEdit(null);
    setShowFolderModal(true);
  };

  const handleEditFolder = (folder) => {
    setFolderToEdit(folder);
    setShowFolderModal(true);
  };

  const handleTagToggle = (tagId) => {
  setFilters(prev => ({
    ...prev,
    tagIds: prev.tagIds.includes(tagId)
      ? prev.tagIds.filter(id => id !== tagId)
      : [...prev.tagIds, tagId]
  }));
};

  const filteredCharts = getFilteredCharts();

  useEffect(() => {
    loadCharts();
  }, []);

  if (loading) {
    return (
      <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[calc(100vh-4rem)]">
        <FolderSidebar
          selectedFolderId={filters.folderId}
          onFolderSelect={handleFolderSelect}
          onCreateFolder={handleCreateFolder}
          onEditFolder={handleEditFolder}
        />
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-semibold tracking-tight text-white">
                Charts
              </h1>
            </div>
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
            <Button onClick={loadCharts}>
              <RefreshCw className="w-4 h-4" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Folder Sidebar */}
      <FolderSidebar
        selectedFolderId={filters.folderId}
        onFolderSelect={handleFolderSelect}
        onCreateFolder={handleCreateFolder}
        onEditFolder={handleEditFolder}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-white">
                Charts
              </h1>
              <p className="text-sm text-[#a0a0a0] mt-1">
                {charts.length} chart{charts.length !== 1 ? 's' : ''} • Create and manage your data visualizations
              </p>
            </div>
            <div className="flex items-center gap-3">
              {isAnalystOrHigher() && (
              <Button variant="secondary" onClick={handleCreateFolder}>
                <Folder className="w-4 h-4" />
                New Folder
              </Button>
              )}
              {isAnalystOrHigher() && (
              <Button variant="secondary" onClick={() => setShowTagsModal(true)}>
                <Tag className="w-4 h-4" />
                Manage Tags
              </Button>
              )}
              <Button
                variant="secondary"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              {isAnalystOrHigher() && (
              <Button onClick={handleNewChart}>
                <Plus className="w-4 h-4" />
                New Chart
              </Button>
              )}
            </div>
          </div>

          {/* Step 11: charts from the previous builder, with a link to each chart's page to update it */}
          {charts.some((c) => !isBuilderChart(c)) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-blue-500/30 bg-blue-500/5 px-4 py-3 text-sm text-[#e5e5e5]">
              <Sparkles className="h-4 w-4 shrink-0 text-blue-400" aria-hidden="true" />
              <span>
                {(() => { const n = charts.filter((c) => !isBuilderChart(c)).length; return `${n} chart${n === 1 ? '' : 's'} from the previous builder can be updated to draw real data.`; })()}
              </span>
              <span className="text-[#a0a0a0]">Open a chart and press Update:</span>
              {charts.filter((c) => !isBuilderChart(c)).slice(0, 5).map((c) => (
                <button key={c.id} type="button" onClick={() => navigate(`/charts/${c.id}`)} className="underline hover:text-white">{c.title}</button>
              ))}
              {charts.filter((c) => !isBuilderChart(c)).length > 5 && <span className="text-[#6a6a6a]">and more below</span>}
            </div>
          )}

          {/* Filters Bar */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search Input */}
            <div className="relative flex-1 min-w-[240px]">
              <input
                type="text"
                placeholder="Search charts..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full h-9 px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] text-white text-sm rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors"
              />
            </div>

            {/* Chart Type Filter */}
            <select
              value={filters.chartType}
              onChange={(e) => handleFilterChange('chartType', e.target.value)}
              className="h-9 px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] text-white text-sm rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors"
            >
              <option value="">All Chart Types</option>
              <option value="line">Line</option>
              <option value="bar">Bar</option>
              <option value="area">Area</option>
            </select>

            {/* Visibility Filter */}
            <select
              value={filters.visibility}
              onChange={(e) => handleFilterChange('visibility', e.target.value)}
              className="h-9 px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] text-white text-sm rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors"
            >
              <option value="">All Visibility</option>
              <option value="private">Private</option>
              <option value="public">Public</option>
            </select>

            {/* Clear Filters */}
  {(filters.search || filters.chartType || filters.visibility || filters.folderId || filters.tagIds.length > 0) && (
    <button
      onClick={() => setFilters({ search: '', chartType: '', visibility: '', folderId: null, tagIds: [] })}
      className="text-xs text-[#6a6a6a] hover:text-white transition-colors"
    >
      Clear filters
    </button>
  )}
</div>

{/* Tag Filter Chips */}
<TagFilterChips
  selectedTags={filters.tagIds}
  onTagToggle={handleTagToggle}
/>

          {deleteError && <p role="alert" className="mb-4 text-sm text-red-400">{deleteError}</p>}

          {/* Chart Grid */}
          {filteredCharts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCharts.map((chart) => (
                <ChartCard key={chart.id} chart={chart} onRefresh={loadCharts} onDelete={removeChart} />
              ))}
            </div>
          ) : charts.length > 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-[#a0a0a0]">No charts match your filters</p>
            </div>
          ) : (
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
              <EmptyState
                icon={BarChart3}
                title="No charts yet"
                description="Get started by creating your first chart to visualize your data"
                actionLabel="New Chart"
                onAction={handleNewChart}
              />
            </div>
          )}

          {/* Modals */}
          <FolderModal
            isOpen={showFolderModal}
            onClose={() => {
              setShowFolderModal(false);
              setFolderToEdit(null);
            }}
            folder={folderToEdit}
            onSuccess={() => {
              setShowFolderModal(false);
              setFolderToEdit(null);
              loadCharts();
            }}
          />

          <ManageAllTagsModal
            isOpen={showTagsModal}
            onClose={() => setShowTagsModal(false)}
            chartTitle="Manage All Tags"
            onUpdate={() => {
              loadCharts();
            }}
          />
          <ChartInUseDialog request={inUse} busy={deleting} onCancel={() => setInUse(null)}
            onConfirm={() => { const r = inUse; setInUse(null); removeChart({ id: r.chartId, title: r.chartTitle }, true); }} />
        </div>
      </div>
    </div>
  );
}
function ChartPreviewIframe({ chart, apiBase }) {
  const [previewToken, setPreviewToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchToken() {
      // Public charts use their embed_token directly
      if (chart.visibility === 'public' && chart.embed_token) {
        setPreviewToken(chart.embed_token);
        setLoading(false);
        return;
      }
      
      // Private charts: fetch a short-lived preview token
      try {
        const res = await apiCall(`/charts/${chart.id}/preview-token`);
        if (res.ok) {
          const data = await res.json();
          setPreviewToken(data.preview_token);
        }
      } catch (err) {
        console.error('Failed to fetch preview token:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchToken();
  }, [chart.id, chart.visibility, chart.embed_token]);

  if (loading) return <div className="h-full flex items-center justify-center text-xs text-[#6a6a6a]">Loading preview...</div>;
  if (!previewToken) return <div className="h-full flex items-center justify-center text-xs text-[#6a6a6a]">Preview unavailable</div>;

  const src = `${apiBase}/embed/chart/${chart.id}?viz=${chart.chart_type}&theme=dark&token=${previewToken}`;

  return (
    <iframe
      src={src}
      width="100%"
      height="100%"
      frameBorder="0"
      title={chart.title}
    />
  );
}
// ==========================================
// CHART CARD COMPONENT
// ==========================================
function ChartCard({ chart, onRefresh, onDelete }) {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);

  const handleClick = (e) => {
    if (e.target.closest('[data-dropdown]')) {
      return;
    }
    navigate(`/charts/${chart.id}`);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    setShowDropdown(false);
    navigate(editPath(chart));
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setShowDropdown(false);
    
    if (!window.confirm(`Delete "${chart.title}"? This cannot be undone.`)) {
      return;
    }
    onDelete(chart);      // 14a-3: the page runs the delete, so a dashboard's refusal can be shown and overridden
  };

  const handleDuplicate = async (e) => {
    e.stopPropagation();
    setShowDropdown(false);

    try {
      const res = await apiCall(`/charts/${chart.id}/duplicate`, { method: 'POST' });
      if (res.ok) {
        onRefresh();
      } else {
        alert('Failed to duplicate chart');
      }
    } catch (err) {
      console.error('Duplicate failed:', err);
      alert('Failed to duplicate chart: ' + err.message);
    }
  };

  const handleCopyEmbed = async (e) => {
    e.stopPropagation();
    setShowDropdown(false);

    try {
      const res = await apiCall(`/charts/${chart.id}/share-link`);
      if (res.ok) {
        const data = await res.json();
        await navigator.clipboard.writeText(data.embed_code);
        alert('Embed code copied to clipboard!');
      }
    } catch (err) {
      console.error('Copy embed failed:', err);
      alert('Failed to copy embed code');
    }
  };

  return (
    <div
      onClick={handleClick}
      className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 hover:bg-[#1e1e1e] hover:border-[#3a3a3a] transition-all cursor-pointer relative"
    >
      <div className="absolute top-4 right-4 z-10" data-dropdown>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowDropdown(!showDropdown);
          }}
          className="p-2 hover:bg-[#2a2a2a] rounded-lg transition-colors"
        >
          <MoreVertical className="w-4 h-4 text-[#a0a0a0]" />
        </button>

        {showDropdown && (
          <>
            <div
              className="fixed inset-0 z-20"
              onClick={(e) => {
                e.stopPropagation();
                setShowDropdown(false);
              }}
            />
            <div className="absolute right-0 mt-2 w-48 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg shadow-xl z-30">
              {isAnalystOrHigher() && (

                <button
                onClick={handleEdit}
                className="w-full px-4 py-2 text-left text-sm text-white hover:bg-[#2a2a2a] transition-colors flex items-center gap-2"
              >
                <Edit2 className="w-4 h-4" />
                Edit Chart
              </button>

              )}
              {isAnalystOrHigher() && (
                <button
                  onClick={handleDuplicate}
                  className="w-full px-4 py-2 text-left text-sm text-white hover:bg-[#2a2a2a] transition-colors flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Duplicate
                </button>
              )}
              <button
                onClick={handleCopyEmbed}
                className="w-full px-4 py-2 text-left text-sm text-white hover:bg-[#2a2a2a] transition-colors flex items-center gap-2"
              >
                <Code2 className="w-4 h-4" />
                Copy Embed Code
              </button>
              {isAnalystOrHigher() && (
                <>
                  <div className="border-t border-[#2a2a2a] my-1" />
                  <button
                    onClick={handleDelete}
                    className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Chart Preview */}
      <div className="w-full h-48 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg mb-4 overflow-hidden relative">
        {isBuilderChart(chart) ? (
          <div className="pointer-events-none h-full w-full bg-[color:var(--chart-surface)] px-3 pt-2">
            <ChartView chartId={chart.id} height={184} showTableToggle={false} {...viewProps(chart)} />
          </div>
        ) : (
          <div className="pointer-events-none w-full h-full" style={{ transform: 'scale(0.95)', transformOrigin: 'center' }}>
            <ChartPreviewIframe chart={chart} apiBase={API_BASE_URL} />
          </div>
        )}
      </div>


      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white truncate">
            {chart.title}
          </h3>
          <Badge variant="neutral">
            {chart.chart_type}
          </Badge>
        </div>

        <div className="flex items-center gap-4 text-xs text-[#6a6a6a]">
          <span>👁 {chart.view_count || 0} views</span>
          <span>•</span>
          <span>{formatDate(chart.last_modified_at)}</span>
        </div>
      </div>
    </div>
  );
}

function formatDate(isoString) {
  if (!isoString) return 'Never';
  
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  } catch {
    return 'Unknown';
  }
}